/**
 * Regras e Constantes Oficiais da Reforma Tributária (EC 132/2023 & LC 214/2025)
 * Fonte: Manual de Orientação da NF-e v2026, Anexo I CBNA e Diretrizes Técnicas R-MAX.
 */
/**
 * Julgamento Histórico do TCU — Alíquota de Referência da CBS (Processo TC 006.529/2026-7)
 * Data da Sessão Plenária: 28 de outubro de 2026
 * Relator: Ministro Vital do Rêgo
 * Base Legal: Artigo 349 da LC nº 214/2025 e Resolução-TCU nº 388/2026
 * Tramitação: Encaminhado à Mesa Diretora do Senado Federal (prazo regimental até 15 de dezembro)
 */
export const TCU_REFORMA_JUDGMENT_2026 = {
    processNumber: 'TC 006.529/2026-7',
    judgmentDate: '2026-10-28',
    tribunal: 'Plenário do Tribunal de Contas da União (TCU)',
    rapporteur: 'Ministro Vital do Rêgo',
    legalBasis: 'Lei Complementar nº 214/2025 (art. 349) e Resolução-TCU nº 388/2026',
    senateDeadline: '2026-12-15',
    cbsRateExact: 11.3468,
    cbsRateRounded: 11.3,
    fazendaOriginalEstimate: 8.8,
    cbsDeltaPp: 2.5,
    publicProcurementReductionExactPct: 44.16,
    publicProcurementReductionRoundedPct: 44.2,
    ibsEstimatedRate: 16.7,
    combinedStandardTeto: 28.0,
    officialCitation: 'TCU — Acórdão TC 006.529/2026-7 (LC 214/2025 art. 349)',
};
// 1. Alíquotas de Referência
export const TAX_REFORM_RATES = {
    // Alíquota de Teste Exclusiva para 2026 (Compensável contra PIS/Cofins)
    PILOT_2026: {
        CBS: 0.9,
        IBS: 0.1,
        TOTAL: 1.0,
    },
    // Projeção Preliminar Ministério da Fazenda (EC 132/2023)
    FAZENDA_PRELIMINAR: {
        CBS_FEDERAL: 8.8,
        IBS_SUBNACIONAL: 17.7,
        COMBINED_TETO: 26.5,
    },
    // Fixação Técnica Oficial TCU (Acórdão TC 006.529/2026-7 - 28/10/2026)
    TCU_BENCHMARK_2026: {
        CBS_FEDERAL: 11.3,
        CBS_FEDERAL_EXACT: 11.3468,
        IBS_SUBNACIONAL: 16.7,
        COMBINED_TETO: 28.0,
        PUBLIC_PROCUREMENT_REDUCTION: 44.2,
    },
    // Alíquotas de Referência Plena (a partir de 2027) — Benchmark Oficial TCU 2026
    STANDARD_2027: {
        CBS_FEDERAL: 11.3,
        IBS_SUBNACIONAL: 16.7,
        COMBINED_TETO: 28.0,
    },
    // Regimes Favorecidos com Redução Legal
    FAVORED_REGIMES: {
        CBNA_ZERO: {
            name: 'Cesta Básica Nacional (CBNA)',
            reductionPercent: 100,
            effectiveRate: 0.0,
            keepsInputCredits: true, // Manutenção integral dos créditos das entradas
            cst: '200',
        },
        HEALTH_EDUCATION_60: {
            name: 'Saúde, Educação e Insumos Agropecuários',
            reductionPercent: 60,
            effectiveRate: 10.6,
            cst: '200',
        },
        FOOD_SERVICES_REST_40: {
            name: 'Bares, Restaurantes e Alimentação Preparada',
            reductionPercent: 40,
            effectiveRate: 15.9,
            tipDeductionLimitPercent: 15, // Dedução de gorjetas até 15%
            cst: '02',
        },
        LIBERAL_PRO_ACADEMIAS_30: {
            name: 'Profissionais Liberais Regulamentados e Academias',
            reductionPercent: 30,
            effectiveRate: 18.55,
            cst: '02',
        },
    },
};
// 1.1 Códigos de Situação Tributária (CST) da Reforma Tributária do Consumo (RTC - NT 2024.001)
export const RTC_CST_CODES = {
    TRIBUTADA_INTEGRAL: '01',
    ALIQUOTA_REDUZIDA: '02',
    CESTA_BASICA_ZERO: '03',
    IMUNIDADE: '04',
    SUSPENSAO: '05',
    NAO_INCIDENCIA: '06',
    SIMPLES_NACIONAL: '50',
};
/**
 * Retorna as alíquotas efetivas de CBS e IBS conforme o ano vigente, benchmark e regime
 */
export function getEffectiveTaxRates(options) {
    const year = options?.year ?? new Date().getFullYear();
    const benchmarkSource = options?.benchmarkSource ?? 'tcu_2026';
    const regimeKey = options?.favoredRegimeKey;
    // Ano de Teste (2026)
    if (year <= 2026) {
        return {
            cbsRate: TAX_REFORM_RATES.PILOT_2026.CBS, // 0.9%
            ibsRate: TAX_REFORM_RATES.PILOT_2026.IBS, // 0.1%
            totalRate: TAX_REFORM_RATES.PILOT_2026.TOTAL, // 1.0%
            isPilotYear: true,
            legacyPisCofinsActive: true, // PIS/Cofins ainda existem e compensam esse 1%
            legacyIcmsIssActive: true,
            benchmarkUsed: benchmarkSource,
            officialSourceCitation: 'Art. 349 LC 214/2025 (Teste Piloto 2026)',
        };
    }
    // A partir de 2027:
    const isFazenda = benchmarkSource === 'fazenda_preliminar_8_8';
    let baseCbs = isFazenda
        ? TAX_REFORM_RATES.FAZENDA_PRELIMINAR.CBS_FEDERAL
        : TAX_REFORM_RATES.TCU_BENCHMARK_2026.CBS_FEDERAL; // 11.3% (TCU TC 006.529/2026-7)
    let baseIbs = TAX_REFORM_RATES.PILOT_2026.IBS; // 0.1% em 2027 e 2028
    const standardIbs = isFazenda
        ? TAX_REFORM_RATES.FAZENDA_PRELIMINAR.IBS_SUBNACIONAL
        : TAX_REFORM_RATES.TCU_BENCHMARK_2026.IBS_SUBNACIONAL;
    if (year >= 2029 && year <= 2032) {
        // Transição gradual do IBS substituindo ICMS e ISS (10% ao ano)
        const transitionFactor = (year - 2028) * 0.1; // 2029 = 0.1, 2030 = 0.2...
        baseIbs = Number((standardIbs * transitionFactor).toFixed(2));
    }
    else if (year >= 2033) {
        // 2033+: Plena
        baseIbs = standardIbs;
    }
    // Aplicação de regimes favorecidos (se aplicável)
    if (regimeKey && TAX_REFORM_RATES.FAVORED_REGIMES[regimeKey]) {
        const favored = TAX_REFORM_RATES.FAVORED_REGIMES[regimeKey];
        const factor = 1 - (favored.reductionPercent / 100);
        baseCbs = Number((baseCbs * factor).toFixed(2));
        baseIbs = Number((baseIbs * factor).toFixed(2));
    }
    return {
        cbsRate: baseCbs,
        ibsRate: baseIbs,
        totalRate: Number((baseCbs + baseIbs).toFixed(2)),
        isPilotYear: false,
        legacyPisCofinsActive: false, // Extintos em 2027!
        legacyIcmsIssActive: year < 2033,
        benchmarkUsed: benchmarkSource,
        officialSourceCitation: isFazenda
            ? 'Ministério da Fazenda — Projeção Preliminar EC 132/2023 (CBS 8,8%)'
            : TCU_REFORMA_JUDGMENT_2026.officialCitation,
    };
}
/**
 * Aplica o redutor legal de compras públicas (art. 349 LC 214/2025 e Resolução-TCU nº 388/2026)
 * Redutor fixado pelo TCU em 44,16% (arredondado para 44,2%)
 */
export function getGovernmentProcurementRates(baseRates) {
    const factor = 1 - (TCU_REFORMA_JUDGMENT_2026.publicProcurementReductionRoundedPct / 100);
    return {
        cbsRateGov: Number((baseRates.cbsRate * factor).toFixed(2)),
        ibsRateGov: Number((baseRates.ibsRate * factor).toFixed(2)),
        totalGovRate: Number(((baseRates.cbsRate + baseRates.ibsRate) * factor).toFixed(2)),
        reductionPercent: TCU_REFORMA_JUDGMENT_2026.publicProcurementReductionRoundedPct,
        reductionExactPercent: TCU_REFORMA_JUDGMENT_2026.publicProcurementReductionExactPct,
        legalCitation: 'LC 214/2025 art. 349 e Resolução-TCU nº 388/2026 (Redutor 44,2%)',
    };
}
export const CBNA_ALÍQUOTA_ZERO_NCMS = [
    { ncmPrefix: '1006.20', name: 'Arroz' },
    { ncmPrefix: '1006.30', name: 'Arroz' },
    { ncmPrefix: '1006.40', name: 'Arroz Quebrado / Fragmentado' },
    { ncmPrefix: '0401.10', name: 'Leite In Natura / Pasteurizado' },
    { ncmPrefix: '0401.20', name: 'Leite Integral' },
    { ncmPrefix: '0401.50', name: 'Leite Desnatado / Semi' },
    { ncmPrefix: '0402.10', name: 'Leite em Pó' },
    { ncmPrefix: '0402.21', name: 'Leite em Pó sem adição de açúcar' },
    { ncmPrefix: '0402.29', name: 'Outros leites em pó' },
    { ncmPrefix: '0713.33', name: 'Feijões (Comum, Preto, Carioca, Fradinho)' },
    { ncmPrefix: '0713.35', name: 'Feijão-fradinho' },
    { ncmPrefix: '0407', name: 'Ovos de Aves' },
    { ncmPrefix: '0201', name: 'Carnes Bovinas Frescas ou Refrigeradas' },
    { ncmPrefix: '0202', name: 'Carnes Bovinas Congeladas' },
    { ncmPrefix: '0203', name: 'Carne Suína' },
    { ncmPrefix: '0204', name: 'Carne Ovina e Caprina' },
    { ncmPrefix: '0207', name: 'Carnes de Aves (Frango, Peru)' },
    {
        ncmPrefix: '0302',
        name: 'Peixes Frescos (Exceto Salmão/Bacalhau)',
        notes: 'Exceção legal: Salmonídeos, atuns, bacalhaus, hadoque e saithe são tributados normalmente'
    },
    { ncmPrefix: '0303', name: 'Peixes Congelados' },
    { ncmPrefix: '0304', name: 'Filés de Peixes' },
    { ncmPrefix: '1106.20', name: 'Farinha de Mandioca' },
    { ncmPrefix: '1102.20', name: 'Fubá de Milho' },
    { ncmPrefix: '1905.90.90', name: 'Pão Francês Tradicional' },
    { ncmPrefix: '0901', name: 'Café Torrado ou Moído' },
    { ncmPrefix: '1507.90', name: 'Óleo de Soja Refinado' },
    { ncmPrefix: '2501.00', name: 'Sal Iodado' },
    { ncmPrefix: '1901.10', name: 'Fórmulas Infantis e Dietoterápicas' },
    { ncmPrefix: '0406', name: 'Queijos Tradicionais (Mozarela, Minas, Prato, Coalho, Ricota, Requeijão)' },
    { ncmPrefix: '1902', name: 'Massas Alimentícias / Macarrão' },
];
// ============================================================================
// 2.1 TABELAS OFICIAIS E SETS O(1) DA LEI COMPLEMENTAR 214/2025
// ============================================================================
// --- A. Imposto Seletivo (IS - Anexo XVII LC 214/2025) ---
export const IMPOSTO_SELETIVO_NCMS = [
    '22021000', // Bebidas açucaradas (refrigerantes, energéticos, refrescos)
    '2601', '27090010', '27111100', '27112100', // Bens minerais extraídos
    '8703', '870421', '870431', '87044100', '87045100', '87046000', '87049000', // Veículos automotores
    '8802', '8903' // Aeronaves e embarcações
];
export const IMPOSTO_SELETIVO_PREFIXOS_NCM = [
    '2203', '2204', '2205', '2206', '2208', // Bebidas alcoólicas (cervejas, vinhos, destilados)
    '2401', '2402', '2403', '2404' // Produtos fumígenos e cigarros eletrônicos
];
export const IMPOSTO_SELETIVO_SET = new Set(IMPOSTO_SELETIVO_NCMS.map(c => c.replace(/[^0-9]/g, '')));
/**
 * Verifica se um NCM está sujeito ao Imposto Seletivo (IS)
 */
export function isImpostoSeletivoNcm(rawNcm) {
    if (!rawNcm)
        return false;
    const cleanNcm = rawNcm.replace(/[^0-9]/g, '');
    if (cleanNcm.length < 4)
        return false;
    if (IMPOSTO_SELETIVO_SET.has(cleanNcm))
        return true;
    if (IMPOSTO_SELETIVO_NCMS.some(item => cleanNcm.startsWith(item)))
        return true;
    return IMPOSTO_SELETIVO_PREFIXOS_NCM.some(prefix => cleanNcm.startsWith(prefix));
}
// --- B. Cesta Básica Nacional (CBNA - Anexos I e XV LC 214/2025 - Alíquota Zero) ---
export const CESTA_BASICA_NCMS = [
    '04051000', '15171000', '07133319', '07133329', '07133399', '07133590',
    '15132120', '11062000', '19030000', '11022000', '11031300', '11041900',
    '11042300', '11010010', '17011400', '17019900', '19059090', '19012010',
    '19012090', '11041200', '11042200', '11029000', '04061010', '04061090',
    '04062000', '04069010', '04069020', '04069030', '25010020', '25010090',
    '07020000', '07070000'
];
export const CESTA_BASICA_SUBPOSICOES_PREFIXOS = [
    '100620', '100630', '100640', // Arroz
    '040110', '040120', '040140', '040150', // Leite fluido
    '040210', '040221', '040229', // Leite em pó
    '190110', '210690', // Fórmulas infantis
    '0901', '21011', // Café
    '19021', // Massas / Macarrão
    '0201', '0202', '0203', '0204', '0207', // Carnes
    '0302', '0303', '0304', // Peixes
    '04072', // Ovos
    '0701', '0703', '0704', '0705', '0706', '0708', '0709', '0710', // Hortaliças e verduras
    '0803', '0804', '0805', '0806', '0807', '0808', '0809', '0810', '0811', '08011' // Frutas
];
export const CESTA_BASICA_SET = new Set(CESTA_BASICA_NCMS.map(c => c.replace(/[^0-9]/g, '')));
/**
 * Verifica se um NCM pertence à Cesta Básica Nacional (Alíquota Zero / Redução 100%)
 */
export function isCbnaZeroTaxNcm(rawNcm, description = '') {
    if (!rawNcm)
        return false;
    const cleanNcm = rawNcm.replace(/[^0-9]/g, '');
    if (cleanNcm.length < 4)
        return false;
    // Exceção expressa da LC 214/2025: Peixes nobres, frutos do mar e iguarias de luxo são tributados
    const lowerDesc = description.toLowerCase();
    if (lowerDesc.includes('salmão') ||
        lowerDesc.includes('salmon') ||
        lowerDesc.includes('bacalhau') ||
        lowerDesc.includes('foie gras') ||
        lowerDesc.includes('lagosta') ||
        lowerDesc.includes('caviar')) {
        return false;
    }
    // 1. Verificação O(1) no Set oficial de NCMs exatos
    if (CESTA_BASICA_SET.has(cleanNcm))
        return true;
    // 2. Verificação de subposições e prefixos oficiais
    if (CESTA_BASICA_SUBPOSICOES_PREFIXOS.some(prefix => cleanNcm.startsWith(prefix))) {
        return true;
    }
    // 3. Fallback para lista legada
    return CBNA_ALÍQUOTA_ZERO_NCMS.some(item => cleanNcm.startsWith(item.ncmPrefix.replace(/[^0-9]/g, '')));
}
// --- C. NCMs com Redução de 60% de CBS/IBS (Anexos IV, VI, VIII e IX LC 214/2025) ---
export const REDUCAO_60_NCMS = [
    // Higiene Pessoal e Limpeza (Anexo VIII)
    '34011190', '33061000', '96032100', '48181000', '38089419', '34011900', '96190000',
    // Insumos Agropecuários e Fertilizantes (Anexo IX)
    '31010000', '38249977', '38249979', '38249989', '23099000',
    // Dispositivos Médicos Frequentes (Anexo IV)
    '40141000', '30069110', '90219012'
];
export const REDUCAO_60_PREFIXOS_NCM = [
    '3808', // Defensivos agropecuários e desinfetantes
    '3002', // Vacinas e imunobiológicos
    '3004', // Medicamentos em doses
    '4015', // Luvas cirúrgicas e de procedimento
    '9018', // Instrumentos médicos, seringas, agulhas e cateteres
    '9021', // Aparelhos ortopédicos e próteses
    '3101', '3102', '3103', '3104', '3105' // Fertilizantes e adubos químicos
];
export const REDUCAO_60_SET = new Set(REDUCAO_60_NCMS.map(c => c.replace(/[^0-9]/g, '')));
/**
 * Verifica se um NCM tem benefício de Redução de 60% nas alíquotas de IBS e CBS
 */
export function isReducao60Ncm(rawNcm) {
    if (!rawNcm)
        return false;
    const cleanNcm = rawNcm.replace(/[^0-9]/g, '');
    if (cleanNcm.length < 4)
        return false;
    if (REDUCAO_60_SET.has(cleanNcm))
        return true;
    return REDUCAO_60_PREFIXOS_NCM.some(prefix => cleanNcm.startsWith(prefix));
}
/**
 * Classifica um produto pelo seu NCM retornando o enquadramento fiscal, alíquota e CST RTC
 */
export function classifyNcmTaxTreatment(rawNcm, description = '') {
    const cleanNcm = (rawNcm || '').replace(/[^0-9]/g, '');
    // 1. Cesta Básica Nacional (Alíquota Zero / Redução 100%)
    if (isCbnaZeroTaxNcm(cleanNcm, description)) {
        return {
            ncmClean: cleanNcm,
            treatment: 'CBNA_ZERO',
            cstRtc: RTC_CST_CODES.CESTA_BASICA_ZERO, // '03'
            reductionPercent: 100,
            hasImpostoSeletivo: false,
            cClassTribSuggested: '000001',
            ruleName: 'Cesta Básica Nacional (Alíquota Zero - Anexos I e XV LC 214/2025)',
        };
    }
    const isIS = isImpostoSeletivoNcm(cleanNcm);
    // 2. Redução de 60% (Saúde, Medicamentos, Insumos Agropecuários, Higiene)
    if (isReducao60Ncm(cleanNcm)) {
        return {
            ncmClean: cleanNcm,
            treatment: 'REDUCAO_60',
            cstRtc: RTC_CST_CODES.ALIQUOTA_REDUZIDA, // '02'
            reductionPercent: 60,
            hasImpostoSeletivo: isIS,
            cClassTribSuggested: '000002',
            ruleName: 'Regime Favorecido com Redução de 60% (Anexos IV, VI, VIII e IX LC 214/2025)',
        };
    }
    // 3. Imposto Seletivo
    if (isIS) {
        return {
            ncmClean: cleanNcm,
            treatment: 'IMPOSTO_SELETIVO',
            cstRtc: RTC_CST_CODES.TRIBUTADA_INTEGRAL, // '01'
            reductionPercent: 0,
            hasImpostoSeletivo: true,
            cClassTribSuggested: '000003',
            ruleName: 'Incidência de Imposto Seletivo - IS (Anexo XVII LC 214/2025)',
        };
    }
    // 4. Tributação Padrão (Integral)
    return {
        ncmClean: cleanNcm,
        treatment: 'STANDARD',
        cstRtc: RTC_CST_CODES.TRIBUTADA_INTEGRAL, // '01'
        reductionPercent: 0,
        hasImpostoSeletivo: false,
        cClassTribSuggested: '000000',
        ruleName: 'Tributação Padrão IBS/CBS',
    };
}
/**
 * Algoritmo Oficial de Custo Unitário Líquido Real (CMV) para Entrada de Insumos (2027)
 */
export function calculateNetUnitCost(params) {
    const { grossTotal, quantity, freight = 0, insurance = 0, otherExpenses = 0, discount = 0, cbsCreditAmount = 0, ibsCreditAmount = 0, } = params;
    const validQty = Math.max(quantity, 0.0001);
    const totalCredits = Math.max(0, cbsCreditAmount + ibsCreditAmount);
    // Fórmula: Custo Líquido = (Valor Bruto + Encargos - Descontos) - Créditos de Impostos
    const grossWithExpenses = grossTotal + freight + insurance + otherExpenses - discount;
    const netTotalCost = Math.max(0, grossWithExpenses - totalCredits);
    const netUnitCost = netTotalCost / validQty;
    return {
        netTotalCost: Number(netTotalCost.toFixed(2)),
        netUnitCost: Number(netUnitCost.toFixed(4)),
        totalCreditsRecoverable: Number(totalCredits.toFixed(2)),
    };
}
/**
 * Apura os créditos fiscais de CBS e IBS de um item adquirido
 */
export function calculateItemTaxCredits(params) {
    const { grossTotal, ncm = '', description = '', cbsCreditExplicit, ibsCreditExplicit, year = new Date().getFullYear(), } = params;
    if (grossTotal <= 0) {
        return {
            cbsCredit: 0,
            ibsCredit: 0,
            totalCredits: 0,
            isCbna: false,
            isReducao60: false,
            hasImpostoSeletivo: false,
            netTotal: 0,
        };
    }
    const isCbna = isCbnaZeroTaxNcm(ncm, description);
    const isReducao60 = isReducao60Ncm(ncm);
    const hasIS = isImpostoSeletivoNcm(ncm);
    // Se o item é Cesta Básica Nacional (Alíquota Zero de compra), não há débito cobrado no item
    if (isCbna) {
        return {
            cbsCredit: 0,
            ibsCredit: 0,
            totalCredits: 0,
            isCbna: true,
            isReducao60: false,
            hasImpostoSeletivo: false,
            netTotal: Number(grossTotal.toFixed(2)),
        };
    }
    // Se vier destacado explicitamente na nota fiscal
    if (cbsCreditExplicit !== undefined || ibsCreditExplicit !== undefined) {
        const cbs = Math.max(0, cbsCreditExplicit || 0);
        const ibs = Math.max(0, ibsCreditExplicit || 0);
        const total = Number((cbs + ibs).toFixed(2));
        const net = Math.max(0, Number((grossTotal - total).toFixed(2)));
        return {
            cbsCredit: cbs,
            ibsCredit: ibs,
            totalCredits: total,
            isCbna: false,
            isReducao60,
            hasImpostoSeletivo: hasIS,
            netTotal: net,
        };
    }
    // Caso padrão: estima com base nas alíquotas legais do ano
    const rates = getEffectiveTaxRates({ year });
    let effectiveCbsRate = rates.cbsRate;
    let effectiveIbsRate = rates.ibsRate;
    // Se o NCM pertence a insumos favorecidos com 60% de redução (Saúde, Medicamentos, Agro, Higiene)
    if (isReducao60) {
        const factor = 1 - 0.60; // 40% da alíquota plena
        effectiveCbsRate = Number((effectiveCbsRate * factor).toFixed(2));
        effectiveIbsRate = Number((effectiveIbsRate * factor).toFixed(2));
    }
    const cbs = Number(((grossTotal * effectiveCbsRate) / 100).toFixed(2));
    const ibs = Number(((grossTotal * effectiveIbsRate) / 100).toFixed(2));
    const total = Number((cbs + ibs).toFixed(2));
    const net = Math.max(0, Number((grossTotal - total).toFixed(2)));
    return {
        cbsCredit: cbs,
        ibsCredit: ibs,
        totalCredits: total,
        isCbna: false,
        isReducao60,
        hasImpostoSeletivo: hasIS,
        netTotal: net,
    };
}
